import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { take } from 'lodash'
import PolarAreaChart from '../../Charts/PolarAreaChart'

interface GeoData {
  type: 'state' | 'city'
  location_name: string
  impressions: number
}

interface GeoImpressionsChartProps {
  data?: GeoData[]
  isLoading?: boolean
  isPreviousData?: boolean
  showGeo: boolean
}

const defaultGeoType = { label: 'state' as const, value: 'state' as const }

const GeoImpressionsChart: React.FC<GeoImpressionsChartProps> = ({
  data = [],
  isLoading = false,
  isPreviousData = false,
  showGeo,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const geoParamValue = searchParams.get('geoType') as 'state' | 'city' | null

  const targetValue =
    showGeo && (geoParamValue === 'state' || geoParamValue === 'city')
      ? geoParamValue
      : defaultGeoType.value

  const [geoType, setGeoType] = useState<{
    label: 'state' | 'city'
    value: 'state' | 'city'
  }>(() => ({ label: targetValue, value: targetValue }))

  useEffect(() => {
    if (geoType.value !== targetValue)
      setGeoType({ label: targetValue, value: targetValue })
  }, [targetValue, geoType.value])

  const handleSelectChange = useCallback(
    (selectedOption: { label: 'state' | 'city'; value: 'state' | 'city' } | null) => {
      if (selectedOption) {
        const currentGeoParam = searchParams.get('geoType')
        if (selectedOption.value !== currentGeoParam) {
          const newParams = new URLSearchParams(searchParams.toString())
          newParams.set('geoType', selectedOption.value)
          setSearchParams(newParams, { replace: true })
        }
      }
    },
    [searchParams, setSearchParams],
  )

  if (!showGeo) return null

  const chartData =
    take(
      data
        ?.filter((d) => d.type === geoType.value)
        ?.map((item: { location_name: string; impressions: number }) => ({
          label: item.location_name,
          value: item.impressions,
        })),
      7,
    ) ?? []

  return (
    <div className={'min-h-[270px] border-2 rounded-xl'}>
      <div
        className={
          'pt-5 pb-2 px-5 flex justify-between items-center font-bold text-sm text-primary-xiketic'
        }>
        <span>Impressions by {geoType.label}</span>
        <div className="w-fit print:hidden">
          <Select
            name={'geo_type_select'}
            options={[
              { label: 'state', value: 'state' },
              { label: 'city', value: 'city' },
            ]}
            value={geoType}
            onChange={handleSelectChange} // Handler only updates URL param
            styles={{
              control: (base) => ({
                ...base,
                border: '2px',
                minWidth: 185,
                minHeight: 36,
                fontFamily: 'Spartan',
                fontWeight: 'bold',
                fontSize: 12,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              }),
              valueContainer: (base) => ({
                ...base,
                minHeight: 36,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: 36,
              }),
              menu: (base) => ({
                ...base,
                width: 'max-content',
                minWidth: '100%',
                fontFamily: 'Spartan',
                fontWeight: 'bold',
                fontSize: 12,
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color: '#09121F', // Custom colour
                marginTop: 0,
              }),

              clearIndicator: (base) => ({
                ...base,
                marginTop: 4,
                color: '#09121F',
              }),
            }}
            menuPlacement="auto"
            isSearchable={false}
          />
        </div>
      </div>
      <div className={'h-[270px] p-2'}>
        <PolarAreaChart
          data={chartData}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
        />
      </div>
    </div>
  )
}

export default GeoImpressionsChart
